import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom';
import { LayoutDashboard, FolderKanban, Settings, LogOut, ScanEye, Sparkles, ShieldCheck, Wrench, Database } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { usePlan, formatUsage, usageFraction } from '@/lib/usePlan';
import { TrialBanner } from '@/components/TrialBanner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

function SidebarLink({ to, icon: Icon, children, testId }) {
  return (
    <NavLink
      to={to}
      end
      data-testid={testId}
      className={({ isActive }) => `sidebar-link ${isActive ? 'sidebar-link-active' : ''}`}
    >
      <Icon className="h-4 w-4" />
      <span>{children}</span>
    </NavLink>
  );
}

function UsageBar({ label, used, limit, testId }) {
  const frac = usageFraction(used, limit);
  const atLimit = limit !== null && limit !== undefined && (used ?? 0) >= limit;
  return (
    <div data-testid={testId}>
      <div className="flex items-center justify-between text-[11px]">
        <span className="text-muted-foreground">{label}</span>
        <span className={atLimit ? 'text-rose-600 font-semibold' : 'text-foreground tabular font-medium'}>
          {formatUsage(used, limit)}
        </span>
      </div>
      {limit !== null && limit !== undefined && (
        <Progress value={frac * 100} className="h-1.5 mt-1" />
      )}
    </div>
  );
}

export default function AppShell() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const plan = usePlan();
  const initials = (user?.name || user?.email || 'U')
    .split(' ')
    .map((p) => p[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  return (
    <div className="flex min-h-screen bg-background">
      {/* Sidebar */}
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-[264px] flex-col border-r bg-card md:flex">
        <div className="flex h-16 items-center gap-2 px-5 border-b">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))]">
            <ScanEye className="h-5 w-5" />
          </div>
          <div>
            <div className="font-display text-base font-semibold tracking-tight">GEOrush</div>
            <div className="text-[11px] text-muted-foreground -mt-0.5">AEO Visibility</div>
          </div>
        </div>
        <nav className="flex flex-col gap-1 p-3">
          <SidebarLink to="/app/dashboard" icon={LayoutDashboard} testId="sidebar-nav-dashboard-link">Dashboard</SidebarLink>
          <SidebarLink to="/app/projects" icon={FolderKanban} testId="sidebar-nav-projects-link">Projects</SidebarLink>
          <SidebarLink to="/app/settings" icon={Settings} testId="sidebar-nav-settings-link">Settings</SidebarLink>
          {user?.is_admin && (
            <SidebarLink to="/app/admin" icon={ShieldCheck} testId="sidebar-nav-admin-link">Admin</SidebarLink>
          )}
          {user?.is_admin && (
            <SidebarLink to="/app/admin/citetrack" icon={Database} testId="sidebar-nav-citetrack-link">CiteTrack</SidebarLink>
          )}
        </nav>
        <div className="mt-auto p-3 border-t space-y-2">
          {/* Plan + usage widget */}
          <div className="rounded-xl border bg-secondary/60 p-3" data-testid="sidebar-plan-widget">
            <div className="flex items-center justify-between">
              <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Plan</div>
              <Badge variant="outline" className="text-[10px]" data-testid="sidebar-plan-badge">
                {plan.plan_name || 'Free'}
              </Badge>
            </div>
            {plan.plan_key === 'free' ? (
              <div className="mt-2 space-y-2">
                <div className="text-[11px] text-muted-foreground leading-snug">
                  Subscribe to start creating projects and running scans.
                </div>
                <Button
                  size="sm"
                  className="w-full h-8"
                  onClick={() => navigate('/app/billing/upgrade')}
                  data-testid="sidebar-upgrade-button"
                >
                  <Sparkles className="h-3.5 w-3.5 mr-1.5" /> Upgrade
                </Button>
              </div>
            ) : plan.usage && plan.limits ? (
              <div className="mt-2 space-y-2" data-testid="sidebar-usage-details">
                <UsageBar
                  label="Scans"
                  used={plan.usage.scans_this_period}
                  limit={plan.limits.scans_per_month}
                  testId="sidebar-usage-scans"
                />
                <UsageBar
                  label="Projects"
                  used={plan.usage.projects}
                  limit={plan.limits.projects}
                  testId="sidebar-usage-projects"
                />
                {plan.plan_key !== 'enterprise' && (
                  <button
                    onClick={() => navigate('/app/billing/upgrade')}
                    className="text-[11px] text-[hsl(var(--accent))] hover:underline"
                    data-testid="sidebar-manage-plan-link"
                  >
                    Manage plan →
                  </button>
                )}
              </div>
            ) : null}
          </div>

          {/* Live providers chip block */}
          <div className="rounded-xl border bg-secondary/60 p-3">
            <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Live Providers</div>
            <div className="mt-2 flex flex-wrap gap-1.5">
              <span className="provider-chip bg-emerald-50 text-emerald-800 border-emerald-200">ChatGPT</span>
              <span className="provider-chip bg-orange-50 text-orange-800 border-orange-200">Claude</span>
              <span className="provider-chip bg-sky-50 text-sky-800 border-sky-200">Gemini</span>
            </div>
          </div>
        </div>
      </aside>

      {/* Main area */}
      <div className="min-w-0 flex-1 md:pl-[264px]">
        {/* Topbar */}
        <header className="sticky top-0 z-20 flex h-16 items-center justify-between border-b bg-card/85 backdrop-blur px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 md:hidden">
            <Link to="/app/dashboard" className="flex items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))]">
                <ScanEye className="h-4 w-4" />
              </div>
              <span className="font-display font-semibold">GEOrush</span>
            </Link>
          </div>
          <div className="hidden md:block text-sm text-muted-foreground">
            Track your brand across ChatGPT, Claude & Gemini answers.
          </div>
          <div className="flex items-center gap-3">
            {user?.is_developer && (
              <Badge
                variant="outline"
                className="gap-1 border-[hsl(var(--accent))]/40 bg-[hsl(var(--accent))]/10 text-[hsl(var(--accent))] text-[10px] font-semibold uppercase tracking-wide"
                data-testid="topbar-developer-badge"
                title="This account has full access and bypasses billing"
              >
                <Wrench className="h-3 w-3" /> Developer
              </Badge>
            )}
            <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="topbar-user-menu-button"
                className="flex items-center gap-2 rounded-full border border-transparent px-2 py-1 hover:border-border"
              >
                <Avatar className="h-8 w-8">
                  <AvatarFallback className="bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] text-xs">
                    {initials}
                  </AvatarFallback>
                </Avatar>
                <div className="hidden text-left sm:block">
                  <div className="text-sm font-medium leading-none">{user?.name || 'User'}</div>
                  <div className="text-xs text-muted-foreground">{user?.email}</div>
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>My Account</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate('/app/settings')} data-testid="user-menu-settings">
                <Settings className="mr-2 h-4 w-4" /> Settings
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => {
                  logout();
                  navigate('/');
                }}
                data-testid="user-menu-logout"
              >
                <LogOut className="mr-2 h-4 w-4" /> Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          </div>
        </header>

        <main className="page-container">
          <TrialBanner />
          <Outlet />
        </main>
      </div>
    </div>
  );
}
